import { Copy, Key, Plus, ShieldAlert, ShieldOff, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useDataProvider, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ApiKey } from "../providers/supabase/dataProvider";
import type { CrmDataProvider } from "../providers/types";

/**
 * ApiKeysSection — pestaña/tarjeta de "Llaves API" en Settings.
 *
 * Permite al admin generar API keys que los agentes IA externos pueden usar
 * para llamar la `meetings-api` (REST) y el MCP server. La key plana solo
 * se ve UNA VEZ al crearla — después en BD solo queda el sha256.
 *
 * Flujo:
 *   1. Click "Crear nueva" → diálogo con name + scopes
 *   2. Submit → backend genera, devuelve el secret
 *   3. Modal muestra el secret con botón copiar (highlight rojo: "esta es la única vez")
 *   4. Al cerrar, vuelve a la lista y el secret desaparece
 */
export function ApiKeysSection() {
  const dp = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [showSecret, setShowSecret] = useState<{
    key: string;
    name: string;
  } | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await dp.apiKeysList();
      setKeys(data);
    } catch (e) {
      notify((e as Error).message, { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async (name: string, scopes: string[]) => {
    try {
      const result = await dp.apiKeysCreate({ name, scopes });
      setShowSecret({ key: result.key, name: result.name });
      setCreateOpen(false);
      await refresh();
    } catch (e) {
      notify((e as Error).message, { type: "warning" });
    }
  };

  const handleRevoke = async (id: number) => {
    if (!confirm("¿Revocar esta API key? Los agentes que la usen dejarán de funcionar al instante.")) return;
    try {
      await dp.apiKeysRevoke(id);
      notify("API key revocada", { type: "success" });
      await refresh();
    } catch (e) {
      notify((e as Error).message, { type: "warning" });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("¿Eliminar esta API key permanentemente?")) return;
    try {
      await dp.apiKeysDelete(id);
      notify("API key eliminada", { type: "success" });
      await refresh();
    } catch (e) {
      notify((e as Error).message, { type: "warning" });
    }
  };

  return (
    <Card id="api-keys">
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            <h2 className="text-xl font-semibold m-0">Llaves API</h2>
          </div>
          <Button onClick={() => setCreateOpen(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" /> Crear nueva
          </Button>
        </div>

        <p className="text-sm text-muted-foreground -mt-2">
          Estas llaves dan acceso programático a la <code>meetings-api</code> y al
          servidor MCP. Entrégaselas a un agente IA (Claude, ChatGPT, OpenClaw…) o
          a un script externo para que pueda crear y editar juntas en tu nombre.
          <br />
          <strong>Importante:</strong> la llave completa solo se muestra una vez al
          crearla. Después solo queda el prefix visible.
        </p>

        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : keys.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <Key className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Aún no hay llaves API. Crea una para conectar un agente.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <KeyRow
                key={k.id}
                k={k}
                onRevoke={() => handleRevoke(k.id)}
                onDelete={() => handleDelete(k.id)}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Diálogo crear */}
      <CreateKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
      />

      {/* Diálogo "secret mostrado UNA SOLA VEZ" */}
      <Dialog
        open={!!showSecret}
        onOpenChange={(o) => !o && setShowSecret(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Copia esta llave ahora
            </DialogTitle>
            <DialogDescription>
              "{showSecret?.name}" — esta es la <strong>única vez</strong> que
              verás la llave completa. Después solo queda el prefix en el listado.
              Guárdala en tu gestor de secretos / variables de entorno del agente.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Input
              readOnly
              value={showSecret?.key ?? ""}
              className="font-mono text-sm pr-12 select-all"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2"
              onClick={() => {
                if (showSecret?.key) {
                  navigator.clipboard.writeText(showSecret.key);
                  notify("Llave copiada al portapapeles", { type: "success" });
                }
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSecret(null)}>
              Listo, ya la copié
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function KeyRow({
  k,
  onRevoke,
  onDelete,
}: {
  k: ApiKey;
  onRevoke: () => void;
  onDelete: () => void;
}) {
  const isRevoked = !!k.revoked_at;
  const isExpired = k.expires_at && new Date(k.expires_at) < new Date();
  const status = isRevoked
    ? "Revocada"
    : isExpired
    ? "Expirada"
    : "Activa";
  return (
    <div
      className={
        "flex items-center gap-3 p-3 rounded-lg border " +
        (isRevoked || isExpired ? "opacity-60" : "")
      }
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold truncate">{k.name}</span>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider"
            style={
              isRevoked || isExpired
                ? { background: "var(--muted)", color: "var(--muted-foreground)" }
                : { background: "var(--musco-accent)", color: "var(--musco-accent-foreground)" }
            }
          >
            {status}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-3">
          <span className="font-mono">{k.prefix}</span>
          <span>Scopes: {k.scopes.join(", ")}</span>
          <span>
            Último uso:{" "}
            {k.last_used_at
              ? new Date(k.last_used_at).toLocaleString("es-MX")
              : "nunca"}
          </span>
        </div>
      </div>
      {!isRevoked && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRevoke}
          title="Revocar (deshabilita la key sin eliminarla)"
        >
          <ShieldOff className="h-4 w-4" />
        </Button>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onDelete}
        title="Eliminar permanentemente"
        style={{ color: "var(--musco-hot)" }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function CreateKeyDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSubmit: (name: string, scopes: string[]) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [scopeRead, setScopeRead] = useState(true);
  const [scopeWrite, setScopeWrite] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setScopeRead(true);
      setScopeWrite(true);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Crear nueva llave API</DialogTitle>
          <DialogDescription>
            Asígnale un nombre descriptivo (p. ej. "Agente WhatsApp Max" o
            "Script importador 2026") para que sepas a quién pertenece y puedas
            revocarla individualmente si algo va mal.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="api-key-name">Nombre</Label>
            <Input
              id="api-key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Agente captura juntas — Max"
            />
          </div>
          <div className="space-y-2">
            <Label>Permisos</Label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scopeRead}
                onChange={(e) => setScopeRead(e.target.checked)}
              />
              <code>meetings:read</code> — listar y obtener juntas
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scopeWrite}
                onChange={(e) => setScopeWrite(e.target.checked)}
              />
              <code>meetings:write</code> — crear, editar, eliminar juntas
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || submitting || (!scopeRead && !scopeWrite)}
            onClick={async () => {
              setSubmitting(true);
              const scopes = [
                ...(scopeRead ? ["meetings:read"] : []),
                ...(scopeWrite ? ["meetings:write"] : []),
              ];
              try {
                await onSubmit(name.trim(), scopes);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? "Creando…" : "Crear llave"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
